import axios from 'axios';
import { ApiResponse } from '../types/api';
import { handleApiResponse, handleApiError } from '../utils/standardApiUtils';
import { API_BASE_URL } from '../config';

// API URL 설정
const API_URL = API_BASE_URL;

// 회원 타입 정의
export interface Member {
  id: number;
  name: string;
  birth_date: string;
  gender: string;
  blood_type: string;
  phone: string;
  email?: string;
  guardian_name?: string;
  guardian_phone?: string;
  address: string;
  current_wearable_id?: string | null;
  current_wearable_code?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  leave_date?: string | null;
}

// 회원 생성 요청 타입
export interface MemberCreate {
  name: string;
  birth_date?: string;
  birthDate?: string;
  gender: string;
  blood_type?: string;
  bloodType?: string;
  phone: string;
  email?: string;
  guardian_name?: string;
  guardianName?: string;
  guardian_phone?: string;
  guardianPhone?: string;
  address: string;
  created_at?: string;
  leave_date?: string;
  leaveDate?: string;
}

// 회원 업데이트 요청 타입
export interface MemberUpdate {
  name?: string;
  birth_date?: string;
  birthDate?: string;
  gender?: string;
  blood_type?: string;
  bloodType?: string;
  phone?: string;
  email?: string;
  guardian_name?: string;
  guardianName?: string;
  guardian_phone?: string;
  guardianPhone?: string;
  address?: string;
  created_at?: string;
  leave_date?: string;
  leaveDate?: string;
}

// 웨어러블 매핑 타입
export interface MemberWearableMapping {
  id: number;
  member_id: number;
  wearable_id: number;
  assigned_at: string;
  returned_at?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

// 필드명 변환 함수
const convertToSnakeCase = (data: any) => {
  const fieldMapping: { [key: string]: string } = {
    birthDate: 'birth_date',
    bloodType: 'blood_type',
    guardianName: 'guardian_name',
    guardianPhone: 'guardian_phone',
    createdAt: 'created_at',
    leaveDate: 'leave_date'
  };

  const convertedData: any = {};
  for (const [key, value] of Object.entries(data)) {
    const snakeCaseKey = fieldMapping[key] || key;
    convertedData[snakeCaseKey] = value;
  }
  return convertedData;
};

// memberApi 객체 정의
export const memberApi = {
  /**
   * 회원 목록 조회
   */
  getMembers: async (poolId: string, page: number = 1, limit: number = 10, search?: string): Promise<ApiResponse<Member[]>> => {
    try {
      console.log(`[memberApi] 회원 목록 조회 시작 - 시설 ID: ${poolId}, 페이지: ${page}, 검색어: ${search}`);
      const response = await axios.get(`${API_URL}/pools/${poolId}/members`, {
        params: { page, limit, search }
      });
      return handleApiResponse<Member[]>(response);
    } catch (error) {
      console.error(`[memberApi] 회원 목록 조회 실패:`, error);
      return handleApiError<Member[]>(error);
    }
  },

  /**
   * 회원 상세 정보 조회
   */
  getMember: async (poolId: number, memberId: number): Promise<ApiResponse<Member>> => {
    try {
      console.log(`[memberApi] 회원 상세 정보 조회 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}`);
      const response = await axios.get(`${API_URL}/pools/${poolId}/members/${memberId}`);
      return handleApiResponse<Member>(response);
    } catch (error) {
      console.error(`[memberApi] 회원 상세 정보 조회 실패:`, error);
      return handleApiError<Member>(error);
    }
  },

  /**
   * 새 회원 등록
   */
  createMember: async (poolId: number, memberData: MemberCreate): Promise<ApiResponse<Member>> => {
    try {
      console.log(`[memberApi] 새 회원 등록 시작 - 시설 ID: ${poolId}`);
      const payload = convertToSnakeCase(memberData);
      const response = await axios.post(`${API_URL}/pools/${poolId}/members`, payload);
      return handleApiResponse<Member>(response);
    } catch (error) {
      console.error(`[memberApi] 새 회원 등록 실패:`, error);
      return handleApiError<Member>(error);
    }
  },

  /**
   * 회원 정보 수정
   */
  updateMember: async (poolId: number, memberId: number, memberData: MemberUpdate): Promise<ApiResponse<Member>> => {
    try {
      console.log(`[memberApi] 회원 정보 수정 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}`);
      const payload = convertToSnakeCase(memberData);
      const response = await axios.put(`${API_URL}/pools/${poolId}/members/${memberId}`, payload);
      return handleApiResponse<Member>(response);
    } catch (error) {
      console.error(`[memberApi] 회원 정보 수정 실패:`, error);
      return handleApiError<Member>(error);
    }
  },

  /**
   * 회원 삭제
   */
  deleteMember: async (poolId: number, memberId: number): Promise<ApiResponse<void>> => {
    try {
      console.log(`[memberApi] 회원 삭제 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}`);
      const response = await axios.delete(`${API_URL}/pools/${poolId}/members/${memberId}`);
      return handleApiResponse<void>(response);
    } catch (error) {
      console.error(`[memberApi] 회원 삭제 실패:`, error);
      return handleApiError<void>(error);
    }
  },

  /**
   * 회원에게 웨어러블 할당
   */
  assignWearableToMember: async (poolId: number, memberId: number, wearableId: number): Promise<ApiResponse<MemberWearableMapping>> => {
    try {
      console.log(`[memberApi] 웨어러블 할당 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}, 웨어러블 ID: ${wearableId}`);
      const response = await axios.post(`${API_URL}/pools/${poolId}/members/${memberId}/wearables`, { wearable_id: wearableId });
      return handleApiResponse<MemberWearableMapping>(response);
    } catch (error) {
      console.error(`[memberApi] 웨어러블 할당 실패:`, error);
      return handleApiError<MemberWearableMapping>(error);
    }
  },

  /**
   * 회원의 웨어러블 반납
   */
  returnWearableFromMember: async (poolId: number, memberId: number, mappingId: number): Promise<ApiResponse<void>> => {
    try {
      console.log(`[memberApi] 웨어러블 반납 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}, 매핑 ID: ${mappingId}`);
      const response = await axios.put(`${API_URL}/pools/${poolId}/members/${memberId}/wearables/${mappingId}/return`);
      return handleApiResponse<void>(response);
    } catch (error) {
      console.error(`[memberApi] 웨어러블 반납 실패:`, error);
      return handleApiError<void>(error);
    }
  },

  /**
   * 회원의 웨어러블 사용 이력 조회
   */
  getMemberWearableHistory: async (poolId: number, memberId: number): Promise<ApiResponse<MemberWearableMapping[]>> => {
    try {
      console.log(`[memberApi] 웨어러블 사용 이력 조회 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}`);
      const response = await axios.get(`${API_URL}/pools/${poolId}/members/${memberId}/wearables`);
      return handleApiResponse<MemberWearableMapping[]>(response);
    } catch (error) {
      console.error(`[memberApi] 웨어러블 사용 이력 조회 실패:`, error);
      return handleApiError<MemberWearableMapping[]>(error);
    }
  },

  /**
   * 회원의 웨어러블 사용 이력 삭제
   */
  clearMemberWearableHistory: async (poolId: number, memberId: number): Promise<ApiResponse<void>> => {
    try {
      console.log(`[memberApi] 웨어러블 사용 이력 삭제 시작 - 시설 ID: ${poolId}, 회원 ID: ${memberId}`);
      const response = await axios.delete(`${API_URL}/pools/${poolId}/members/${memberId}/wearables`);
      return handleApiResponse<void>(response);
    } catch (error) {
      console.error(`[memberApi] 웨어러블 사용 이력 삭제 실패:`, error);
      return handleApiError<void>(error);
    }
  }
};